import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Chip,
  Slider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, InfoOutlined as InfoIcon, Functions as FunctionsIcon } from '@mui/icons-material';
import { getDailyNorms, updateDailyNorms, resetDailyNorms } from '@services/settings.service.ts';
import { getCalculatedMetricValues, getCalculationParams } from '@services/calculatedMetrics.service.ts';
import { calculateMetric } from '@services/calculated-metrics.service.ts';
import { authService } from '@services/auth.service.ts';
import { DailyNormsData, UpdateDailyNormsRequest } from '@/types/settings.types';
import { toastService } from '@services/toast.service.ts';
import { MetricInfoModal } from '@components/CalculatedMetrics/MetricInfoModal.tsx';
import { CalculationParamsPanel } from '@components/settings/CalculationParamsPanel.tsx';
import { CalculationExplanationModal } from '@components/settings/CalculationExplanationModal.tsx';
import { CalculationParams, CalculationOptions } from '@services/calculatedMetrics.service.ts';
import { TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { PageHint } from '@components/common/PageHint.tsx';

interface AdvancedWaterResult {
  metric: string;
  explanation: string;
  calculatedAt: string;
  result: number;
  resultDetails: {
    resultMl: number;
    resultLiters: number;
    genderCoefficient: number;
    activityMultiplier: number;
    ageMultiplier: number;
    resultComment: string;
    calculationBreakdown: { formula: string; explanation: string; };
    additionalRecommendations: string;
  };
  infoPath: string;
}

export const GoalsSettingsTab: React.FC = () => {
  const [goals, setGoals] = useState<Partial<DailyNormsData>>({});
  // Завантажені норми - щоб надсилати лише змінені поля (бекенд позначає змінений блок як "введено вручну").
  const loadedNorms = useRef<Partial<DailyNormsData>>({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [calculatedMetricValues, setCalculatedMetricValues] = useState<Record<string, number | null>>({});
  const [loadingCalculatedMetrics, setLoadingCalculatedMetrics] = useState(true);
  const [errorCalculatedMetrics, setErrorCalculatedMetrics] = useState<string | null>(null);

  const [calculationParams, setCalculationParams] = useState<Partial<CalculationParams>>({});
  const [calculationOptions, setCalculationOptions] = useState<Partial<CalculationOptions>>({});

  const [advancedWaterResult, setAdvancedWaterResult] = useState<AdvancedWaterResult | null>(null);
  const [advancedWaterLoading, setAdvancedWaterLoading] = useState(false);
  const [advancedWaterError, setAdvancedWaterError] = useState<string | null>(null);

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedMetricForInfo, setSelectedMetricForInfo] = useState<string | null>(null);

  const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);
  const [explanationData, setExplanationData] = useState<{ title: string; explanation: string | null }>({ title: '', explanation: null });

  const fetchGoals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDailyNorms();
      if (response && response.data) {
        setGoals(response.data);
        loadedNorms.current = response.data;
      }
    } catch (err) {
      setError('Не вдалося завантажити денні норми.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalculatedData = async () => {
    try {
      setLoadingCalculatedMetrics(true);
      setErrorCalculatedMetrics(null);
      const user = await authService.getCurrentUser();
      const values = await getCalculatedMetricValues(user.id);
      const { params, options } = await getCalculationParams();

      setCalculatedMetricValues(values);
      setCalculationParams(params);
      setCalculationOptions(options);
    } catch (err) {
      setErrorCalculatedMetrics('Не вдалося завантажити розрахункові показники або параметри.');
      console.error(err);
    } finally {
      setLoadingCalculatedMetrics(false);
    }
  };

  useEffect(() => {
    fetchGoals();
    fetchCalculatedData();
  }, []);

  useEffect(() => {
    const calculateAdvancedWater = async () => {
      if (!calculationParams.weight || !calculationParams.gender || !calculationParams.age || !calculationParams.activityLevel) {
        setAdvancedWaterResult(null);
        return;
      }

      setAdvancedWaterLoading(true);
      setAdvancedWaterError(null);
      try {
        const result = await calculateMetric(
          'wdna',
          {
            weight: calculationParams.weight,
            gender: calculationParams.gender,
            age: calculationParams.age,
            activityLevel: calculationParams.activityLevel,
          }
        );
        setAdvancedWaterResult(result);
      } catch (err: any) {
        setAdvancedWaterError(err.response?.data?.message || 'Не вдалося розрахувати розширену норму води.');
        console.error('Error calculating advanced water:', err);
      } finally {
        setAdvancedWaterLoading(false);
      }
    };
    calculateAdvancedWater();
  }, [calculationParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGoals(prev => ({ ...prev, [name]: value === '' ? null : parseFloat(value) }));
  };

  const handleSliderChange = (name: keyof DailyNormsData, value: number | number[]) => {
    setGoals(prev => ({ ...prev, [name]: value as number }));
  };

  const handleSetRecommended = (field: keyof DailyNormsData, value: number | null) => {
    if (value !== null) {
      setGoals(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleOpenInfoModal = (metric: string) => {
    setSelectedMetricForInfo(metric);
    setIsInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setIsInfoModalOpen(false);
    setSelectedMetricForInfo(null);
  };

  const handleOpenExplanationModal = (title: string, explanation: string | null) => {
    setExplanationData({
      title: title,
      explanation: explanation || 'Пояснення не знайдено.',
    });
    setIsExplanationModalOpen(true);
  };

  const handleCloseExplanationModal = () => {
    setIsExplanationModalOpen(false);
    setExplanationData({ title: '', explanation: null });
  };

  const handleSave = async () => {
    setIsSaving(true);

    // Лише змінені поля; очищене поле - null (норму не задано), 0 - валідне значення.
    const fields = ['waterMl', 'caloriesIntake', 'caloriesBurned', 'proteinG', 'fatG', 'carbsG'] as const;
    const payload: UpdateDailyNormsRequest = {};
    fields.forEach((field) => {
      const value = goals[field] ?? null;
      if (value !== (loadedNorms.current[field] ?? null)) {
        payload[field] = value === null ? null : Number(value);
      }
    });

    try {
      await updateDailyNorms(payload);
      loadedNorms.current = { ...loadedNorms.current, ...payload };
      toastService.success('Денні норми збережено');
    } catch (err: any) {
      const message = Array.isArray(err.response?.data?.message)
        ? err.response?.data?.message.join(', ')
        : 'Не вдалося зберегти денні норми.';
      toastService.error(message);
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsSaving(true);
    try {
      await resetDailyNorms();
      toastService.success('Денні норми видалено - до наступного розрахунку вони не задані');
      fetchGoals();
    } catch (err) {
      toastService.error('Не вдалося видалити денні норми.');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const getHumanReadableMetricName = (name: string): string => {
    switch (name) {
      case 'bmi': return 'Індекс маси тіла (BMI)';
      case 'bmr': return 'Базальний метаболізм (BMR)';
      case 'wdn': return 'Добова норма води (WDN)';
      case 'amr': return 'Активний рівень метаболізму (AMR)';
      case 'whr': return 'Відношення талія/стегна (WHR)';
      default: return name;
    }
  };

  const renderDeviation = (current: number | string | null | undefined, recommended: number | null) => {
    if (current === null || current === undefined || recommended === null || recommended === 0) {
      return null;
    }
    const deviation = ((Number(current) - recommended) / recommended) * 100;
    const color = Math.abs(deviation) <= 10 ? 'success' : Math.abs(deviation) <= 20 ? 'warning' : 'error';
    return <Chip label={`${deviation.toFixed(1)}%`} color={color} size="small" sx={{ ml: 2 }} />;
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const waterNorm = calculatedMetricValues.wdn;
  const amrValue = calculatedMetricValues.amr;
  const otherMetrics = Object.entries(calculatedMetricValues).filter(([key]) => key !== 'wdn' && key !== 'amr');

  const baseWaterGoal = advancedWaterResult?.result ?? waterNorm;
  const sliderMin = baseWaterGoal ? Math.round(baseWaterGoal * 0.8) : 500;
  const sliderMax = baseWaterGoal ? Math.round(baseWaterGoal * 1.5) : 10000;

  const marks = baseWaterGoal ? [{ value: baseWaterGoal, label: `${baseWaterGoal} мл` }] : [];

  return (
    <Box data-i-doc={TabIndices.SETTINGS_GOALS_ADV}>
      <PageHint iDoc={TabIndices.SETTINGS_GOALS_ADV} />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CalculationParamsPanel 
            params={calculationParams}
            options={calculationOptions}
            onParamsChange={setCalculationParams}
            loading={loadingCalculatedMetrics}
            error={errorCalculatedMetrics}
          />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Ціль по воді</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <TextField fullWidth type="number" label="Вода (мл)" name="waterMl" value={goals.waterMl ?? ''} onChange={handleChange} inputProps={{ min: 500, max: 10000 }} />
                  {renderDeviation(goals.waterMl, advancedWaterResult?.result ?? waterNorm)}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                {advancedWaterLoading ? <CircularProgress size={24} /> : 
                 advancedWaterError ? <Alert severity="error">{advancedWaterError}</Alert> : 
                 advancedWaterResult ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="text"
                        onClick={() => handleSetRecommended('waterMl', advancedWaterResult.result)}
                        sx={{ textTransform: 'none', minWidth: 'auto', padding: '6px 8px', fontSize: '1.25rem', fontWeight: 'bold', color: 'text.primary', '&:hover': { backgroundColor: 'action.hover' } }}
                      >
                        {advancedWaterResult.result} мл
                      </Button>
                      <IconButton onClick={() => handleOpenExplanationModal(
                        'Розширена норма води',
                        `Формула: ${advancedWaterResult.resultDetails?.calculationBreakdown?.formula || 'Не знайдено'}\n\n${advancedWaterResult.resultDetails?.calculationBreakdown?.explanation || 'Не знайдено'}`
                      )} size="small">
                        <FunctionsIcon />
                      </IconButton>
                      <IconButton onClick={() => advancedWaterResult?.infoPath && handleOpenInfoModal(advancedWaterResult.infoPath)} size="small">
                        <InfoIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Недостатньо даних для розрахунку розширеної норми води.</Typography>
                  )
                }
              </Grid>
              {baseWaterGoal && (
                <Grid item xs={12} sx={{ px: 1, mt: 2 }}>
                  <Slider
                    value={typeof goals.waterMl === 'number' ? goals.waterMl : 0}
                    onChange={(_, newValue) => handleSliderChange('waterMl', newValue)}
                    aria-labelledby="water-goal-slider"
                    valueLabelDisplay="auto"
                    step={10}
                    min={sliderMin}
                    max={sliderMax}
                    marks={marks}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Ціль по калоріях</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center">
                  <TextField fullWidth type="number" label="Калорії (спожито, ккал)" name="caloriesIntake" value={goals.caloriesIntake ?? ''} onChange={handleChange} inputProps={{ min: 1000, max: 5000 }} />
                  {renderDeviation(goals.caloriesIntake, amrValue)}
                </Box>
              </Grid>
              <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                <IconButton onClick={() => handleSetRecommended('caloriesIntake', amrValue)} disabled={amrValue === null || loadingCalculatedMetrics}><ArrowBackIcon /></IconButton>
              </Grid>
              <Grid item xs={12} sm={6}>
                {loadingCalculatedMetrics ? <CircularProgress size={24} /> : 
                  <Box display="flex" alignItems="center">
                    <ListItemText primary="Активний рівень метаболізму (AMR)" secondary={amrValue !== null ? `${amrValue} ккал` : 'Не розраховано'} />
                    <IconButton onClick={() => handleOpenInfoModal('amr')} size="small"><InfoIcon /></IconButton>
                  </Box>
                }
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Інші показники</Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Спалені калорії (ккал)" name="caloriesBurned" value={goals.caloriesBurned ?? ''} disabled />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Білки (г)" name="proteinG" value={goals.proteinG ?? ''} disabled />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Жири (г)" name="fatG" value={goals.fatG ?? ''} disabled />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Вуглеводи (г)" name="carbsG" value={goals.carbsG ?? ''} disabled />
              </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" gutterBottom>Довідкові розрахункові показники</Typography>
            {loadingCalculatedMetrics ? (
              <CircularProgress />
            ) : errorCalculatedMetrics ? (
              <Alert severity="error">{errorCalculatedMetrics}</Alert>
            ) : (
              <List dense>
                {otherMetrics.map(([key, value]) => (
                  <ListItem key={key}>
                    <ListItemText primary={getHumanReadableMetricName(key)} secondary={value !== null ? value : 'Не розраховано'} />
                    <IconButton onClick={() => handleOpenInfoModal(key)} size="small"><InfoIcon /></IconButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="outlined" color="secondary" onClick={handleReset} disabled={isSaving}>Скинути</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>{isSaving ? <CircularProgress size={24} /> : 'Зберегти'}</Button>
        </Box>
      </Paper>

      <MetricInfoModal
        open={isInfoModalOpen}
        onClose={handleCloseInfoModal}
        metricName={selectedMetricForInfo || ''}
      />

      <CalculationExplanationModal
        open={isExplanationModalOpen}
        onClose={handleCloseExplanationModal}
        title={explanationData.title}
        explanation={explanationData.explanation}
      />
    </Box>
  );
};
